import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { AuthContext } from "../allauth/auth/AuthContext";
import { getAuth } from "../allauth/lib/allauth";
import ApiClient from "../config/ApiClient";
import { useAppContext } from "../context/AppContext";
import { useRoleLabels } from "../hooks/useRoleLabels";
import type { LessonPlanRole } from "../object-actions/types/types";
import { orgAthleteWaiverFile, waiverFileForRole } from "../utils/waiver";

const DEFAULT_WAIVER = "eula.md";
/** Sent as `signature` so the existing backend validation (`I AGREE`) stays unchanged. */
const WAIVER_SIGNATURE = "I agree";

export default function WaiverModal() {
    const { auth } = React.useContext<any>(AuthContext) || {};
    const { organization } = useAppContext();
    const { applyRoleLabels } = useRoleLabels();

    const user = auth?.data?.user;
    const isAuthenticated = auth?.meta?.is_authenticated === true;

    const [pendingWaiver, setPendingWaiver] = useState<{
        role: LessonPlanRole | string | null;
        assignmentId: number | null;
    }>({ role: null, assignmentId: null });
    const [forceOpen, setForceOpen] = useState(false);

    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent).detail as
                | { role?: LessonPlanRole | string; assignmentId?: number }
                | undefined;
            setPendingWaiver({
                role: detail?.role ?? null,
                assignmentId: detail?.assignmentId ?? null,
            });
            setForceOpen(true);
        };
        window.addEventListener("smsp:open-waiver", handler);
        return () => window.removeEventListener("smsp:open-waiver", handler);
    }, []);

    const waiverName = useMemo(() => {
        const orgConfig = organization
            ? {
                waiver_athlete: organization.waiver_athlete,
                waiver_adult: organization.waiver_adult,
            }
            : null;
        const name =
            waiverFileForRole(pendingWaiver.role, orgConfig) ||
            orgAthleteWaiverFile(orgConfig) ||
            DEFAULT_WAIVER;
        return /^[a-zA-Z0-9_.-]+$/.test(name) ? name : DEFAULT_WAIVER;
    }, [pendingWaiver.role, organization]);

    const open = useMemo(() => {
        if (!isAuthenticated || !user) return false;
        return forceOpen;
    }, [isAuthenticated, user, forceOpen]);

    const [markdownContent, setMarkdownContent] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [agreed, setAgreed] = useState(false);

    // Load markdown content
    useEffect(() => {
        if (!open) return;

        setLoading(true);
        setAgreed(false);

        const loadMarkdown = async () => {
            try {
                const response = await fetch(`/waivers/${waiverName}`);
                if (response.ok) {
                    const text = await response.text();
                    setMarkdownContent(text);
                } else {
                    console.error('Failed to load waiver form');
                }
            } catch (error) {
                console.error('Error loading waiver form:', error);
            } finally {
                setLoading(false);
            }
        };

        loadMarkdown();
    }, [open, waiverName]);

    const onAccept = useCallback(async () => {
        if (!user?.id) return;
        if (!agreed) return;

        const payload: Record<string, unknown> = {
            role: pendingWaiver.role,
            signature: WAIVER_SIGNATURE,
        };
        if (pendingWaiver.assignmentId != null) {
            payload.assignment_id = pendingWaiver.assignmentId;
        }

        const resp = await ApiClient.post(`/api/waivers/accept/`, payload);
        if (resp.success) {
            setForceOpen(false);
            setPendingWaiver({ role: null, assignmentId: null });
            window.dispatchEvent(new Event("smsp:waiver-accepted"));
            try {
                await getAuth();
            } catch (e) {
                // ignore
            }
        }
    }, [user, pendingWaiver, agreed]);

    const onClose = useCallback(() => {
        setForceOpen(false);
        setPendingWaiver({ role: null, assignmentId: null });
    }, []);

    if (!open) return null;

    if (loading) {
        return (
            <Dialog open={open} aria-labelledby="waiver-title" maxWidth="md" fullWidth>
                <DialogTitle id="waiver-title">Liability Waiver</DialogTitle>
                <DialogContent>
                    <Typography>Loading waiver form...</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            aria-labelledby="waiver-title"
            maxWidth="md"
            fullWidth
            data-testid="waiver-modal"
            PaperProps={{
                sx: {
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle id="waiver-title">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {applyRoleLabels('Guardian Waiver & Consent Form')}
                    </Typography>
                    <Button variant="text" color="primary" onClick={onClose} data-attr="waiver-modal_close">Close</Button>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ overflowY: 'auto' }}>
                <Box
                    sx={{
                        '& p': { margin: 0 },
                        '& p + p': { marginTop: 2 },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                            marginTop: 3,
                            marginBottom: 2,
                            fontWeight: 'bold',
                            color: 'text.primary'
                        },
                        '& ul, & ol': {
                            marginTop: 2,
                            marginBottom: 2,
                            paddingLeft: 3
                        },
                        '& li': {
                            marginBottom: 1
                        },
                        '& hr': {
                            marginTop: 3,
                            marginBottom: 3,
                            borderColor: 'divider'
                        },
                        '& strong': {
                            fontWeight: 'bold'
                        }
                    }}
                >
                    <ReactMarkdown>{markdownContent}</ReactMarkdown>
                </Box>
                <Box sx={{ my: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Typography variant="body2" >
                        <strong>Name:</strong> {user?.first_name || ''} {user?.last_name || ''}
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={agreed}
                                onChange={(e) => setAgreed(e.target.checked)}
                                data-attr="waiver-modal_field-signature"
                            />
                        }
                        label="I agree that I have read and accept the terms above"
                    />
                    <Typography variant="body2" color="text.secondary">
                        <strong>Date:</strong> {new Date().toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        })}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                    data-attr="waiver-modal_close-footer"
                >
                    Close
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={onAccept}
                    disabled={!agreed}
                    data-attr="waiver-modal_accept"
                >
                    I accept
                </Button>
            </DialogActions>
        </Dialog>
    );
}
